import React, { useState, useRef, useEffect } from "react";
import { SendHorizonal, Sparkles, Loader2 } from "lucide-react";
import { chatWithDoc, getChatThread } from "@/lib/rexxponsApi";

export const FollowUpChat = ({ docId, docKind }) => {
  const [messages, setMessages] = useState([]);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    let active = true;
    getChatThread(docId).then((prior) => {
      if (active && prior.length) {
        setMessages(prior.map((m, i) => ({ ...m, id: `p-${i}` })));
      }
    });
    return () => {
      active = false;
    };
  }, [docId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  const send = async () => {
    const q = value.trim();
    if (!q || busy) return;
    const userMsg = { id: `u-${Date.now()}`, role: "user", content: q };
    const next = [...messages, userMsg];
    setMessages(next);
    setValue("");
    setBusy(true);
    try {
      const reply = await chatWithDoc(docId, docKind, next);
      setMessages([...next, { id: `a-${Date.now()}`, role: "assistant", content: reply }]);
    } catch (e) {
      setMessages([...next, { id: `a-${Date.now()}`, role: "assistant", content: "Sorry — I couldn't answer that just now. Please try again." }]);
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <section data-testid="followup-chat" className="mt-16 border-t border-border pt-10">
      <div className="flex items-center gap-2 text-primary mb-5">
        <Sparkles strokeWidth={1.5} className="w-4 h-4" />
        <h3 className="text-xs tracking-[0.2em] uppercase font-medium">Continue the conversation</h3>
      </div>

      {messages.length === 0 && (
        <p className="text-sm text-muted-foreground mb-5">
          Ask a follow-up question about this report — Rexxpons keeps the full context in memory.
        </p>
      )}

      <div className="space-y-4 mb-5">
        {messages.map((m) => (
          <div
            key={m.id}
            data-testid={m.role === "user" ? "chat-user-msg" : "chat-assistant-msg"}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-primary/15 text-foreground border border-primary/30"
                  : "bg-secondary border border-border text-foreground/90"
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start" data-testid="chat-loading">
            <div className="rounded-2xl px-4 py-3 bg-secondary border border-border text-muted-foreground flex items-center gap-2 text-sm">
              <Loader2 strokeWidth={1.5} className="w-4 h-4 animate-spin" /> Thinking…
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="flex items-end gap-3 bg-secondary/70 border border-border focus-within:border-primary/50 rounded-2xl px-4 py-3">
        <textarea
          data-testid="chat-input"
          rows={1}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask a follow-up…"
          className="flex-1 bg-transparent outline-none border-none resize-none text-sm text-foreground placeholder:text-muted-foreground/60 caret-cyan max-h-32"
        />
        <button
          data-testid="chat-send-btn"
          onClick={send}
          disabled={!value.trim() || busy}
          aria-label="Send"
          className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center bg-primary text-primary-foreground disabled:opacity-30 active:scale-95 hover:bg-primary/90 transition-[background-color,transform]"
        >
          <SendHorizonal strokeWidth={1.75} className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
};
